import { Fragment, useEffect, useMemo, useState } from "react";
import { ExternalLink, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useW } from "./ctx";
import { Hl, hlClass } from "./Popover";
import { FIELD_PRINTED, money, norm } from "./lib";

/* ------------------------------------------------------------------ spec */

function useSourcesByPage(doc) {
  const w = useW();
  return useMemo(() => {
    const m = {};
    Object.values(w.data.sources).forEach((s) => { if (s.doc === doc && s.page) (m[s.page] = m[s.page] || []).push(s); });
    return m;
  }, [w.data.sources, doc]);
}

export function SpecDoc() {
  const w = useW();
  const pages = w.data.docs?.spec || [];
  const byPage = useSourcesByPage("spec");
  return (
    <div className="mx-auto max-w-[860px] space-y-5 px-6 py-6" data-testid="spec-doc">
      {pages.map((p) => <SpecPage key={p.n} p={p} srcs={byPage[p.n] || []} w={w} />)}
    </div>
  );
}

function SpecPage({ p, srcs, w }) {
  const skipped = p.class === "answer_key";
  const paraSrc = (text) => {
    const t = norm(text);
    return srcs.find((s) => !s.field && s.text && t.length > 30 && (norm(s.text) === t || t.includes(norm(s.text)) || norm(s.text).startsWith(t.slice(0, 120))))
      || srcs.find((s) => s.id.startsWith("OS-") && !s.field && t === norm(`Opening ${s.door.replace("-", " ")}`));
  };
  const fieldSrc = (label) => srcs.find((s) => s.field && (FIELD_PRINTED[s.field] || []).includes(norm(label)));
  return (
    <section className={`paper relative px-10 py-8 text-[12.5px] leading-relaxed ${skipped ? "opacity-60" : ""}`} data-page={p.n}>
      <span className="absolute right-4 top-3 mono text-[var(--muted)]">p. {p.n}</span>
      {skipped && <p className="mb-3 rounded-md bg-[var(--amber-bg)] px-3 py-2 text-[12px] font-medium text-[var(--amber)]">Condoor didn't read this page: it's marked as a presenter-only answer key.</p>}
      {(p.blocks || [{ kind: "p", text: p.text || "" }]).map((b, i) => {
        if (b.kind === "table") {
          return (
            <table key={i} className="my-2 w-full border-collapse text-[12px]"><tbody>
              {b.rows.map((r, j) => (
                <tr key={j} className="border-t border-[var(--line)]">
                  {r.map((c, k) => {
                    const left = k % 2 === 1 ? r[k - 1] : null;
                    const s = !skipped && left != null ? fieldSrc(left) : null;
                    return <td key={k} className={`px-2 py-1 align-top ${k % 2 === 0 ? "text-[var(--ink2)]" : ""}`}>
                      {s ? <Hl id={s.id} cls={hlClass(w, s.id)}>{c}</Hl> : c}</td>;
                  })}
                </tr>
              ))}
            </tbody></table>
          );
        }
        const s = !skipped ? paraSrc(b.text) : null;
        const head = i === 0 || /^(Requirement|Submission|Review and resolution|Required records|Revision trace|Component coordination|Supporting documents|Change impact)$/.test(b.text);
        const content = s ? <Hl id={s.id} cls={hlClass(w, s.id)}>{b.text}</Hl> : b.text;
        return i === 0 ? <h3 key={i} className="mb-2 text-[15px] font-semibold">{content}</h3>
          : head ? <p key={i} className="mt-3 font-semibold">{content}</p> : <p key={i} className="my-1 whitespace-pre-line">{content}</p>;
      })}
    </section>
  );
}

export function SpecQuick() {
  const w = useW();
  const { clauses, sets, submittal_contents: contents } = w.data.spec;
  return (
    <div className="mx-auto max-w-[1100px] space-y-6 px-6 py-5">
      <Intro title={`${w.data.spec.doc?.id} · ${w.data.spec.doc?.title || "Specification"}`}
        sub={`${w.data.spec.doc?.pages} pages · ${clauses.length} requirements · ${sets.length} hardware sets · pages ${(w.data.spec.excluded_pages || []).join(", ") || "none"} not read (presenter-only)`} />
      <Table head={["Clause", "Requirement", "Page", "Drives", "Status"]}
        rows={clauses.map((c) => {
          const n = (w.drives[c.id] || []).length;
          const f = w.flagBySource[c.id];
          return { key: c.id, onClick: () => w.openSource(c.id), cells: [<b className="mono">{c.id}</b>, <span><b>{c.title}.</b> <span className="text-[var(--ink2)]">{c.requirement}</span></span>,
            c.page, n ? `${n} lines` : "—", <span className={`badge ${f ? "b-confirm" : n ? "b-resolved" : "b-met"}`}>{f ? "Flag rests on it" : n ? "Met" : "Process"}</span>] };
        })} />
      <Table head={["Set", "Application", "Per opening", "Openings", "Drives"]}
        rows={sets.map((s) => ({ key: s.set, onClick: () => w.openSource(s.set), cells: [<b className="mono">{s.set}</b>, s.application,
          `${s.qty.hinges} hinges · ${s.qty.locks} lock · ${s.qty.closers} closer${s.qty.drop_seals ? ` · ${s.qty.drop_seals} drop seal` : ""}${s.qty.readers ? ` · ${s.qty.readers} reader` : ""}`,
          s.openings.join(", "), `${(w.drives[s.set] || []).length} lines`] }))} />
      <Table head={["What the submittal must contain", "Clause"]} rows={contents.map((c) => ({ key: c.key, cells: [c.text, c.clause] }))} />
    </div>
  );
}

/* ------------------------------------------------------------------ schedule and interfaces (sheets) */

function SheetTables({ docKey, prefix, mappingFields }) {
  const w = useW();
  const pages = w.data.docs?.[docKey] || [];
  return (
    <div className="space-y-6 px-6 py-6">
      {pages.map((p) => (
        <section key={p.n} className="paper overflow-hidden" data-page={p.n}>
          <div className="flex items-center justify-between border-b border-[var(--line)] px-4 py-2 text-[12px] text-[var(--muted)]">
            <span>{p.sheet || `Page ${p.n}`}</span><span className="mono">p. {p.n}</span>
          </div>
          {(p.tables || (p.blocks || []).filter((b) => b.kind === "table").map((b) => b.rows)).map((t, ti) => (
            <div key={ti} className="scroll max-h-[70vh]"><SheetTable t={t} prefix={prefix} mappingFields={mappingFields} /></div>
          ))}
          {p.text && !p.sheet && <Prose text={p.text} />}
        </section>
      ))}
    </div>
  );
}

function SheetTable({ t, prefix, mappingFields }) {
  const w = useW();
  const hi = Math.max(0, t.findIndex((r) => r.some((c) => /^door( id| no)?$/i.test(String(c).trim()))));
  const header = t[hi] || [];
  const title = t.slice(0, hi).filter((r) => r.some((c) => String(c).trim()));
  return (
    <>
      {title.map((r, i) => <p key={i} className={`px-4 ${i === 0 ? "pt-3 text-[14px] font-semibold" : "text-[12px] text-[var(--ink2)]"}`}>{r.filter(Boolean).join(" ")}</p>)}
      <table className="sheet m-4">
        <thead><tr>{header.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
        <tbody>
          {t.slice(hi + 1).map((r, i) => {
            const door = r.map((c) => String(c).match(/\bD-\d{3}\b/)?.[0]).find(Boolean);
            const rowId = door && `${prefix}-${door}`;
            const hasRow = rowId && w.data.sources[rowId];
            return (
              <tr key={i} data-row={door}>
                {r.map((c, k) => {
                  const field = mappingFields?.[header[k]];
                  const fid = rowId && field && `${rowId}#${field}`;
                  if (fid && w.data.sources[fid]) return <td key={k}><Hl id={fid} cls={hlClass(w, fid)}>{c || "blank"}</Hl></td>;
                  if (k === 0 && hasRow) return <td key={k}><Hl id={rowId} cls={hlClass(w, rowId)}>{c}</Hl></td>;
                  return <td key={k}>{c}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function Prose({ text }) {
  const w = useW();
  const notes = Object.values(w.data.sources).filter((s) => s.id.startsWith("SEC-note"));
  const paras = text.split(/\n+/).filter((l) => l.trim() && !l.trim().startsWith("|"));
  return (
    <div className="px-6 pb-5 text-[12.5px] leading-relaxed">
      {paras.map((l, i) => {
        const clean = l.replace(/\*\*/g, "").replace(/^#+\s*/, "");
        const n = notes.find((s) => norm(clean).includes(norm(s.text).slice(0, 80)));
        return <p key={i} className={`my-1 ${/^#/.test(l) ? "mt-3 font-semibold" : ""}`}>{n ? <Hl id={n.id} cls={hlClass(w, n.id)}>{clean}</Hl> : clean}</p>;
      })}
    </div>
  );
}

export function ScheduleDoc() {
  const w = useW();
  return <div data-testid="schedule-doc"><SheetTables docKey="schedule" prefix="A601" mappingFields={w.data.schedule.mapping} /></div>;
}

export function InterfacesDoc() {
  const w = useW();
  // The voltage lives in the Lock column on this sheet.
  const m = Object.fromEntries(Object.entries(w.data.interfaces?.mapping || {}).map(([h, f]) => [h, f === "lock" ? "voltage" : f]));
  return <div data-testid="interfaces-doc"><SheetTables docKey="interfaces" prefix="SEC" mappingFields={m} /></div>;
}

export function ScheduleQuick() {
  const w = useW();
  const decisionDoors = new Set(w.data.lines.filter((l) => l.kind !== "met").map((l) => l.door));
  return (
    <div className="mx-auto max-w-[1100px] px-6 py-5">
      <Intro title={`${w.data.schedule.doc.id} · Door and frame schedule`} sub={`${w.data.schedule.rows.length} openings, each with a floor plan reference`} />
      <Table head={["Door", "Room", "Plan ref", "Set", "Size", "Rating", "Hand", "Remarks", "Drives"]}
        rows={w.data.schedule.rows.map((r) => ({ key: r.door, onClick: () => w.openSource(`A601-${r.door}`), cells: [<b className="mono">{r.door}</b>, r.room, r.plan_ref, r.set,
          `${r.width_in} × ${r.height_in}`, r.rating_min ? `${r.rating_min} min` : "—", r.hand, <span className="text-[var(--ink2)]">{r.remarks}</span>,
          <span className={`badge ${decisionDoors.has(r.door) ? "b-confirm" : "b-met"}`}>{(w.drives[`A601-${r.door}`] || []).length} lines</span>] }))} />
    </div>
  );
}

export function InterfacesQuick() {
  const w = useW();
  const i = w.data.interfaces;
  return (
    <div className="mx-auto max-w-[1100px] px-6 py-5">
      <Intro title={`${i.doc.id} · Security and electrical interfaces`} sub={`${i.rows.length} controlled openings`} />
      <Table head={["Door", "Lock", "Power loss", "Power supply", "Supplied by", "Wired by"]}
        rows={i.rows.map((r) => ({ key: r.door, onClick: () => w.openSource(`SEC-${r.door}`), cells: [<b className="mono">{r.door}</b>, r.lock, r.power_loss, r.power_supply,
          r.supplied_by || <span className="badge b-assumption">blank</span>, r.wired_by || <span className="badge b-assumption">blank</span>] }))} />
    </div>
  );
}

/* ------------------------------------------------------------------ codes */

export function CodeDoc({ id }) {
  const w = useW();
  const d = w.data.codes.find((c) => c.id === id);
  if (!d) return null;
  return (
    <div className="mx-auto max-w-[820px] px-6 py-6">
      <article className="paper px-10 py-8 text-[13px] leading-relaxed" data-testid={`code-doc-${id}`}>
        <span className="badge b-code">Public code · internet</span>
        <h1 className="mt-3 text-[19px] font-semibold">{d.title}</h1>
        <p className="text-[var(--ink2)]">{d.publisher} · {d.edition}</p>
        <div className="mt-2 flex flex-wrap gap-3">{d.links.map((l) => <a key={l.url} href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[12.5px] text-[var(--code)] underline"><ExternalLink size={12} />{l.label}</a>)}</div>
        <p className="mt-3 rounded-md bg-[var(--code-bg)] px-3 py-2 text-[12px] text-[var(--code)]">Paraphrased by Condoor, not the code text. Applies here to {d.applies_to.length} {d.why}. Checked {new Date(d.checked_at).toLocaleString()}.</p>
        {d.sections.map((s) => (
          <section key={s.id} className="mt-5">
            <p className="font-semibold">{s.ref} · {s.title}</p>
            <p className="mt-1">{s.before}{w.data.sources[s.id] ? <Hl id={s.id} cls="hl hl-code">{s.hl}</Hl> : <mark className="bg-[var(--code-bg)]">{s.hl}</mark>}{s.after}</p>
          </section>
        ))}
      </article>
    </div>
  );
}

export function CodeQuick({ id }) {
  const w = useW();
  const d = w.data.codes.find((c) => c.id === id);
  if (!d) return null;
  return (
    <div className="mx-auto max-w-[1000px] px-6 py-5">
      <Intro title={d.title} sub={`${d.edition} · applies to ${d.applies_to.length} ${d.why}`} />
      <Table head={["Section", "What it says (paraphrased)", "Drives"]} rows={d.sections.map((s) => ({ key: s.id, onClick: () => w.data.sources[s.id] && w.openSource(s.id),
        cells: [<b>{s.ref}</b>, `${s.before}${s.hl}${s.after}`, `${(w.drives[s.id] || []).length} lines`] }))} />
    </div>
  );
}

/* ------------------------------------------------------------------ cut sheets */

export function CutDoc({ id }) {
  const w = useW();
  const c = w.data.cut_sheets[id];
  if (!c) return null;
  const sid = `CS-${id}`;
  return (
    <div className="mx-auto max-w-[780px] px-6 py-6">
      <article className="paper px-10 py-8 text-[13px]" data-testid={`cut-doc-${id}`}>
        <div className="flex items-center justify-between text-[11.5px] text-[var(--muted)]"><span>{c.mfr}</span><span>{c.docType}</span></div>
        <h1 className="mt-4 text-[19px] font-semibold">{w.data.sources[sid] ? <Hl id={sid} cls="hl hl-cat">{c.title}</Hl> : c.title}</h1>
        {c.excerpt && <p className="mt-1 text-[12px] text-[var(--ink2)]">Excerpt cut from a larger manual. Only these pages are attached.</p>}
        <table className="mt-5 w-full border-collapse"><tbody>
          {c.rows.map((r, i) => {
            const proves = r[2] || [];
            const fails = r[3] || [];
            return (
              <tr key={i} className={`border-t border-[var(--line)] ${fails.length ? "bg-[var(--red-bg)]" : proves.length ? "bg-[var(--cat-bg)]/60" : ""}`}>
                <td className="w-[150px] py-2 pr-3 font-medium">{r[0]}</td>
                <td className="py-2">{r[1]}
                  <div className="mt-1 flex flex-wrap gap-1">
                    {proves.filter((p) => w.data.sources[p]).map((p) => <button key={p} className="tag tag-code" onClick={() => w.openSource(p)}>proves {w.data.code_labels[p] || p}</button>)}
                    {fails.map((p) => <span key={p} className="tag" style={{ color: "var(--red)", borderColor: "#f6c7bb" }}>fails {w.data.code_labels[p] || p}</span>)}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody></table>
        <p className="mt-6 text-[11px] text-[var(--muted)]">Fictional DEMO product data for a synthetic demonstration.</p>
      </article>
    </div>
  );
}

export function CutQuick({ id }) {
  const w = useW();
  const c = w.data.cut_sheets[id];
  const lines = w.data.lines.filter((l) => l.cut === id);
  return (
    <div className="mx-auto max-w-[1000px] px-6 py-5">
      <Intro title={c.title} sub={`${c.origin} · ${c.docType} · attached for ${lines.length} lines on ${new Set(lines.map((l) => l.door)).size} doors`} />
      <Table head={["Row", "What it proves"]} rows={c.rows.map((r, i) => ({ key: i, cells: [r[0], <span>{r[1]} {(r[3] || []).length ? <span className="badge b-conflict ml-1">counter-evidence</span> : null}</span>] }))} />
      <Table head={["Line", "Item", "Catalog no."]} rows={lines.slice(0, 80).map((l) => ({ key: l.id, onClick: () => w.openLine(l.id, "quick"), cells: [<span className="mono">{l.id}</span>, l.product, l.cat] }))} />
    </div>
  );
}

/* ------------------------------------------------------------------ catalog (supplier only) */

export function Catalog() {
  const w = useW();
  const [cat, setCat] = useState(null);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("All");
  useEffect(() => { api.get("/condoor/catalog").then((r) => setCat(r.data)).catch(() => setCat({ items: [] })); }, []);
  const used = useMemo(() => {
    const m = {};
    w.data.lines.forEach((l) => { m[l.cat] = (m[l.cat] || 0) + 1; });
    return m;
  }, [w.data.lines]);
  if (!cat) return <p className="p-6 text-[var(--muted)]">Loading catalog…</p>;
  const groups = ["All", ...new Set(cat.items.map((i) => i.cat))];
  const rows = cat.items.filter((i) => (group === "All" || i.cat === group) && (!q || norm(`${i.code} ${i.item} ${i.mfr}`).includes(norm(q))));
  return (
    <div className="px-6 py-5" data-testid="catalog-view">
      <Intro title="Acme catalog · all projects" sub={`${cat.items.length} items · supplier only; the architect never sees this file`} />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="flex h-8 items-center gap-2 rounded-full border border-[var(--line2)] bg-white px-3"><Search size={13} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the catalog" className="bg-transparent outline-none" /></label>
        <select value={group} onChange={(e) => setGroup(e.target.value)} className="h-8 rounded-full border border-[var(--line2)] bg-white px-3">{groups.map((g) => <option key={g}>{g}</option>)}</select>
      </div>
      <Table head={["Code", "Item", "Manufacturer", "Finish", "Fire listing", "Price", "Stock", "Lead time", "Warranty", "Lines"]}
        rows={rows.map((i) => ({ key: i.code, onClick: () => i.sheet && w.data.cut_sheets[i.sheet] && w.openDoc(`cut:${i.sheet}`, "doc"),
          cells: [<span className="mono">{i.code}</span>, i.item, i.mfr, i.finish, i.label, money(i.price), i.stock, i.lead, i.warranty,
            used[i.code] ? <span className="badge b-cat">{used[i.code]}</span> : "—"] }))} />
    </div>
  );
}

/* ------------------------------------------------------------------ shared */

function Intro({ title, sub }) {
  return <div className="mb-4"><h2 className="text-[19px] font-semibold">{title}</h2><p className="text-[13px] text-[var(--ink2)]">{sub}</p></div>;
}

function Table({ head, rows }) {
  return (
    <div className="card mb-5 overflow-hidden">
      <table className="grid text-[13px]">
        <thead><tr>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.key} className={r.onClick ? "cursor-pointer hover:bg-[var(--bg)]" : ""} onClick={r.onClick}>
            {r.cells.map((c, i) => <td key={i}>{c}</td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export const _ = Fragment;
